<template>
  <div>
    <Breadcrumbs :items="items" />
    <v-sheet class="transparent-background-color">
      <v-row dense class="ma-2">
        <v-col>
          <v-card>
            <v-card-title>
              <span class="subtitle-1">
                <span class="danger--text font-weight-bold">
                  {{ stock.name }}
                </span>
                {{ $t("DistributionCoverage.undeliveryTitle.part_one") }}
                <span class="danger--text font-weight-bold">
                  {{ district.district_name }}
                </span>
                {{ $t("DistributionCoverage.undeliveryTitle.part_two") }}
              </span>
              <v-spacer />
              <v-btn
                small
                dark
                color="primary"
                class="px-2 me-2 mb-1"
                @click.stop="add()"
              >
                <v-icon x-small class="pe-1" color="white"
                  >fa-angle-double-right</v-icon
                >
                {{ $t("DistributionCoverage.Fields.addAll") }}
              </v-btn>
            </v-card-title>
            <v-data-table
              :headers="undeliveryHeaders"
              :items="undeliveryMaterials"
              :loading="loading"
              :search="undeliveryFilter"
              multi-sort
              class="elevation-2 px-4 mb-4"
              :footer-props="$t('$config.footer-props')"
            >
              <template v-slot:top>
                <v-toolbar
                  flat
                  class="transparent-background-color"
                  align-self="center"
                >
                  <v-row no-gutters class="me-n2 mx-n4">
                    <v-col cols="12" sm="9" md="8">
                      <v-text-field
                        v-model="undeliveryFilter"
                        clearable
                        hide-details
                        :label="$t('Search.filter')"
                      />
                    </v-col>
                  </v-row>
                </v-toolbar>
              </template>
              <template v-slot:header.actions="{ header }">
                {{ header.text }}
                <v-icon x-small>fa-sliders-h</v-icon>
              </template>
              <template v-slot:item.actions="{ item }">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      x-small
                      v-bind="attrs"
                      v-on="on"
                      color="primary"
                      @click.stop="add(item)"
                      class="px-2 me-2 mb-1"
                    >
                      <v-icon x-small color="white">fa-plus</v-icon>
                    </v-btn>
                  </template>
                  <span>{{
                    $t("DistributionCoverage.Fields.removeHint")
                  }}</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
        <v-col>
          <v-card>
            <v-card-title>
              <span class="subtitle-1">
                {{ $t("DistributionCoverage.deliveryTitle") }}
              </span>
              <v-spacer />
              <v-btn
                small
                dark
                color="danger"
                class="px-2 me-2 mb-1"
                @click.stop="remove()"
              >
                <v-icon x-small class="pe-1" color="white"
                  >fa-angle-double-left</v-icon
                >
                {{ $t("DistributionCoverage.Fields.removeAll") }}
              </v-btn>
            </v-card-title>
            <v-data-table
              :headers="deliveryHeaders"
              :items="deliveryMaterials"
              :loading="loading"
              :search="deliveryFilter"
              multi-sort
              class="elevation-2 px-4 py-1 mb-4"
              :footer-props="$t('$config.footer-props')"
            >
              <template v-slot:top>
                <v-toolbar
                  flat
                  class="transparent-background-color"
                  align-self="center"
                >
                  <v-row no-gutters class="me-n2 mx-n4">
                    <v-col cols="12" sm="9" md="8">
                      <v-text-field
                        v-model="deliveryFilter"
                        clearable
                        hide-details
                        :label="$t('Search.filter')"
                      />
                    </v-col>
                  </v-row>
                </v-toolbar>
              </template>
              <template v-slot:header.actions="{ header }">
                {{ header.text }}
                <v-icon x-small>fa-sliders-h</v-icon>
              </template>
              <template v-slot:item.actions="{ item }">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      x-small
                      v-bind="attrs"
                      v-on="on"
                      color="danger"
                      @click.stop="remove(item)"
                      class="px-2 me-2 mb-1"
                    >
                      <v-icon x-small color="white">fa-minus</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t("DistributionCoverage.Fields.addHint") }}</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </v-sheet>

    <DialogAlert v-model="conflictDialog" :message="conflictMsg" />
  </div>
</template>
<script>
import i18n from "@/i18n";
import { mapState } from "vuex";
import Breadcrumbs from "@/components/Breadcrumbs";
import DialogAlert from "@/components/plugins/DialogAlert";
import Stocks from "@/api/stocks";
import DistributionCoverage from "@/api/distribution_coverage";

export default {
  components: {
    Breadcrumbs,
    DialogAlert,
  },

  computed: {
    stockId() {
      return this.stock ? this.stock.id : 0;
    },
    items() {
      return [
        {
          text: i18n.t("Nav.home"),
          disabled: false,
          href: "/home",
        },
        {
          text: i18n.t("Nav.stockManagement"),
          disabled: false,
          href: "/stocks",
        },
        {
          text: i18n.t("Nav.stocks"),
          disabled: false,
          href: "/stocks",
        },
        {
          text: i18n.t("Nav.distributionCoverage"),
          disabled: false,
          href: "/stocks/distribution-coverage?stock_id=" + this.stockId,
        },
        {
          text: i18n.t("Nav.invalidMaterials"),
          disabled: true,
          href: "/stocks/distribution-coverage/invalid-materials",
        },
        {
          text: this.stock && this.stock.name ? this.stock.name : "",
          accent: true,
          disabled: false,
        },
      ];
    },

    undeliveryHeaders: () => [
      {
        text: i18n.t("DistributionCoverage.Fields.material_code"),
        align: "start",
        sortable: false,
        value: "material_code",
      },
      {
        text: i18n.t("DistributionCoverage.Fields.material_name"),
        align: "start",
        sortable: false,
        value: "material_name",
      },
      {
        text: i18n.t("DistributionCoverage.Fields.material_category"),
        align: "center",
        sortable: false,
        value: "material_category.name",
      },
      {
        text: i18n.t("TableHeaders.actions"),
        align: "start",
        sortable: false,
        value: "actions",
      },
    ],

    deliveryHeaders: () => [
      {
        text: i18n.t("DistributionCoverage.Fields.material_code"),
        align: "start",
        sortable: false,
        value: "material.material_code",
      },
      {
        text: i18n.t("DistributionCoverage.Fields.material_name"),
        align: "start",
        sortable: false,
        value: "material.material_name",
      },
      {
        text: i18n.t("DistributionCoverage.Fields.material_category"),
        align: "start",
        sortable: false,
        value: "material.material_category.name",
      },
      {
        text: i18n.t("TableHeaders.actions"),
        align: "start",
        sortable: false,
        value: "actions",
      },
    ],

    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["loading"]),
  },

  data: () => ({
    stock: {},
    district: {},
    undeliveryFilter: "",
    deliveryFilter: "",
    deliveryMaterials: [],
    undeliveryMaterials: [],
    conflictMsg: "",
    conflictDialog: false,
  }),

  mounted() {
    if (this.$route.query.stock_id) {
      Stocks.fetchOne(this.$route.query.stock_id).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.stock = response.data.stock;
          }
        }.bind(this)
      );
    }
  },

  methods: {
    getMaterials() {
      // 获取配送商品列表
      DistributionCoverage.fetchDelivery({
        stock_id: this.stockId,
        district_code: this.$route.query.district_code,
      }).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.deliveryMaterials =
              response.data.validDistributionCenterConfigs;
          }
        }.bind(this)
      );
      // 获取不配送商品
      DistributionCoverage.fetchUndelivery({
        stock_id: this.stockId,
        district_code: this.$route.query.district_code,
      }).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.undeliveryMaterials = response.data.materials;
          }
        }.bind(this)
      );
    },
    //从可配送中移除，添加到不可配送列表
    remove(material) {
      DistributionCoverage.removeMaterial(
        material
          ? {
              stock_id: this.stockId,
              district_code: this.district.district_code,
              material_id: material.material_id,
            }
          : {
              stock_id: this.stockId,
              district_code: this.district.district_code,
            }
      ).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.getMaterials();
          }
        }.bind(this)
      );
    },
    //从不可配送中移除，添加到可配送列表
    add(material) {
      DistributionCoverage.addMaterial(
        material
          ? {
              stock_id: this.stockId,
              district_code: this.district.district_code,
              material_id: material.id,
            }
          : {
              stock_id: this.stockId,
              district_code: this.district.district_code,
            }
      ).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.getMaterials();
            if (response.data.conflicts && response.data.conflicts.length) {
              this.conflictMsg = i18n.t(
                "DistributionCoverage.materialsConflictDialogMsg",
                [response.data.conflicts[0].distribution_center_name]
              );
              this.conflictDialog = true;
            }
          }
        }.bind(this)
      );
    },
  },

  watch: {
    stock: {
      handler() {
        if (this.stock && this.stock.id) {
          if (this.$route.query.district_code) {
            DistributionCoverage.fetchSelected({
              stock_id: this.stockId,
              district_code: this.$route.query.district_code,
            }).then(
              function (response) {
                if (response.data.status.isOK()) {
                  this.district = response.data.districts[0];
                }
              }.bind(this)
            );
          }
          this.getMaterials();
        }
      },
    },
    conflictDialog: {
      deep: true,
      handler() {
        if (!this.conflictDialog && this.conflictMaterials.length) {
          this.conflictMaterials = [];
        }
      },
    },
  },
};
</script>